/**
 * Users slice (SCRUM-1024) — createSlice/createAppAsyncThunk migration of
 * usersReducer + the read half of userActions. Boy-scout migration under the
 * bare-RTK standard (docs/Client_State_And_Data_Fetching_Standard.md): SCRUM-1024
 * already forced work in this reducer, so it migrates now. Mirrors clientSlice.js
 * (SCRUM-1017).
 *
 * userActions.js re-exports everything here (shim), so no component changes.
 *
 * SCRUM-1033 finished the migration: the three write thunks deleteUser /
 * editPermission / editRole moved here too, so no legacy action creators are left
 * in userActions.js. (authActions.js still has six hand-written creators —
 * loginUser / logoutUser / getProfile / verifPassord / updatePassword /
 * changeStatus — which are out of this ticket's scope.)
 *
 * SCRUM-1024 deferred these three because converting changes what a caller catches
 * — `.unwrap()` throws the DISPLAYABLE PAYLOAD (`err.response.data`), not the
 * original axios error the SCRUM-769/813/815 tests pinned. That rewrite is this
 * ticket's scope. Each caller now reads `err?.message` where it read
 * `err?.response?.data?.message`. For a server error that is the same string and
 * the same bespoke fallback copy; for a bare NETWORK error it is a deliberate
 * change — the payload falls back to `{ message: err.message }`, so the user sees
 * "Network Error" where the old read was `undefined` and hit the generic fallback.
 * More informative, and the central hub already showed that text.
 *
 * The GET_USERS string `addCase` stays — that IS getUsers' write path (the SCRUM-924
 * contract its test pins), the same escape hatch clientSlice.js uses for
 * GET_CLIENTS_BY_COUNTRYCODE. The POST_USER / DELETE_USER / UPDATE_USER string cases
 * are gone: nothing dispatches them now that the write thunks own their `.fulfilled`.
 */
import { createSlice } from "@reduxjs/toolkit";
import type { Dispatch, PayloadAction } from "@reduxjs/toolkit";
import type { AxiosResponse } from "axios";
import api from "../api";
import createAppAsyncThunk from "../createAppAsyncThunk";
import { GET_USERS } from "./userTypes";
import type {
  UsersResponse,
  UserRegisterResponse,
  UserRestoreResponse,
  UserReleaseResponse,
  UserResponse,
} from "../../../../shared/apiTypes";

/** The add-user form's draft, written step by step through saveUser. */
type RegisterUserDraft = Record<string, unknown>;

/** The body of a user write (register, permission or role update). */
type UserWriteBody = Record<string, unknown> & { _id?: string };

// The three fields stay `any`, as in the JSDoc this replaces: the user screens
// and the page editors read them untyped (the add-user steps cast
// `register_user` to any; the User containers convert in SCRUM-2044). They
// narrow when their readers do.
type UsersState = {
  users: any[];
  update_user: any;
  register_user: any;
};

const initialState: UsersState = {
  users: [],
  update_user: {},
  register_user: {
    name: "",
    email: "",
    emailverif: "",
    countrycode: [],
    role: "",
    enabled: true,
    permission: [],
  },
};

/**
 * SCRUM-1024: `users` is mapped/filtered by the template and variable-field
 * editors, so a null or object payload used to white-screen them. Never store a
 * non-array.
 */
const asList = (payload: unknown): UsersState["users"] => (Array.isArray(payload) ? payload : []);

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

/**
 * SCRUM-1024: the old hand-written version dispatched the SUCCESS action with
 * `payload: null` on failure, which overwrote the list and white-screened the
 * editor. Now a failure rejects to the central hub and the stored list is left
 * untouched.
 *
 * Converged with SCRUM-924, which converted this thunk to createAppAsyncThunk on
 * premaster in parallel. Its contract is kept intact: the success path still
 * dispatches the legacy `GET_USERS` string (that IS the write path — see the
 * addCase below — and getUsers.errorHandling.test.js pins the dispatch), and the
 * type prefix stays `users/getUsers`. What SCRUM-1024 adds on top is the array
 * coercion: SCRUM-924 fixed the null-on-FAILURE wipe, but a non-array 200 could
 * still poison the list.
 */
export const getUsers = createAppAsyncThunk(
  "users/getUsers",
  async (_arg: void, { dispatch }) => {
    const res: AxiosResponse<UsersResponse> = await api.get("/api/users");
    dispatch({ type: GET_USERS, payload: res.data });
    return res.data;
  }
);

// SCRUM-840: createAppAsyncThunk so a failed register (e.g. a validation 400)
// surfaces via the central hub instead of an unhandled rejection. Callers await
// .unwrap(): success resolves to res.data (they read temporaryPassword off it),
// failure skips the redirect.
export const registerUser = createAppAsyncThunk(
  "users/registerUser",
  async (data: UserWriteBody) => {
    const res: AxiosResponse<UserRegisterResponse> = await api.post("/api/users/register", data);
    return res.data;
  }
);

/**
 * SCRUM-1663: bring a soft-deleted user back under its reserved e-mail. The
 * add-user form offers this when register answers 409 with a `deletedId`; it is
 * an explicit admin action because the restored account keeps its old role and
 * permissions. Resolves to the restored user (no password).
 */
export const restoreUser = createAppAsyncThunk(
  "users/restoreUser",
  async (id: string) => {
    const res: AxiosResponse<UserRestoreResponse> = await api.post(`/api/users/${id}/restore`);
    return res.data;
  }
);

/**
 * SCRUM-1816 "Start fresh": free the e-mail a soft-deleted user keeps reserved,
 * so the add-user form can register a new account under it. The deleted user
 * stays deleted (renamed server-side), so nothing in the list changes here.
 * Resolves to the released user (no password).
 */
export const releaseUser = createAppAsyncThunk(
  "users/releaseUser",
  async (id: string) => {
    const res: AxiosResponse<UserReleaseResponse> = await api.post(`/api/users/${id}/release`);
    return res.data;
  }
);

/**
 * SCRUM-815 hardened this to surface a failed delete instead of letting
 * UsersListContainer run its cleanup + success toast anyway; SCRUM-1033 keeps that
 * guarantee while moving the plumbing to the standard. The failure now reaches the
 * GET_ERRORS hub via appErrorMiddleware rather than a hand-written catch, and the
 * list write happens in `.fulfilled` rather than an explicit DELETE_USER dispatch.
 * Resolves to the deleted id — that is the `.fulfilled` payload the filter reads.
 */
export const deleteUser = createAppAsyncThunk(
  "users/deleteUser",
  async (id: string) => {
    await api.delete<unknown>(`/api/users/${id}`);
    return id;
  }
);

/**
 * SCRUM-769: a failed permission update must not fall through to a silent redirect
 * to /admin/users, and must not write the error body into `update_user`. Both hold
 * here by construction — only `.fulfilled` writes the slice.
 */
export const editPermission = createAppAsyncThunk(
  "users/editPermission",
  async (data: UserWriteBody) => {
    const res: AxiosResponse<UserResponse> = await api.put(`/api/users/updatePermission/${data._id}`, data);
    return res.data;
  }
);

/**
 * SCRUM-813: same guarantee for the role PUT — EditRoleContainer must not update the
 * parent list or close the modal unless this resolves.
 */
export const editRole = createAppAsyncThunk(
  "users/editRole",
  async (data: UserWriteBody) => {
    const res: AxiosResponse<UserResponse | null> = await api.put(`/api/users/updateRole/${data._id}`, data);
    return res.data;
  }
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    userSaved(state, action: PayloadAction<RegisterUserDraft>) {
      state.register_user = action.payload;
    },
    // Legacy shape: the old CLEAR_USER dispatched `payload: {}` and the reducer
    // wrote it straight into register_user, so the reset value is `{}` — NOT the
    // rich initialState.register_user. Preserved exactly.
    userCleared(state) {
      state.register_user = {};
    },
  },
  extraReducers: (builder) => {
    builder
      // NOTE: getUsers has no `.fulfilled` case on purpose — it writes through the
      // GET_USERS string below, which SCRUM-924's contract requires it to dispatch.
      // Adding one here would write the same list twice.
      .addCase(registerUser.fulfilled, (state, action) => {
        state.users = [action.payload, ...asList(state.users)];
      })
      // A restored user re-enters the list the same way a registered one does.
      .addCase(restoreUser.fulfilled, (state, action) => {
        state.users = [action.payload, ...asList(state.users)];
      })
      // SCRUM-1033 — the write thunks now own their own state writes. Only the
      // success case touches the slice, so a failed delete/permission/role update
      // can no longer corrupt it (the SCRUM-769 bug wrote the error body into
      // update_user). `asList` keeps the SCRUM-1024 guard: these run against a list
      // a poisoned read may have left non-array.
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.users = asList(state.users).filter(
          (user) => user._id !== action.payload
        );
      })
      .addCase(editPermission.fulfilled, (state, action) => {
        state.update_user = action.payload;
      })
      .addCase(editRole.fulfilled, (state, action) => {
        state.update_user = action.payload;
      })
      // getUsers writes through this legacy string on purpose — that dispatch IS
      // the SCRUM-924 contract its test pins. See the header note.
      .addCase(GET_USERS, (state, action: PayloadAction<unknown, typeof GET_USERS>) => {
        state.users = asList(action.payload);
      });
  },
});

// Public API keeps the legacy names so call sites are unchanged.
export const saveUser = (register_user: RegisterUserDraft) =>
  usersSlice.actions.userSaved(register_user);
export const clearUser = () => (dispatch: Dispatch) =>
  dispatch(usersSlice.actions.userCleared());

export default usersSlice.reducer;
